import { Component, ViewChild, ElementRef, Inject, Optional, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { EmissionService } from '../../service/emission-service/emission.service';

@Component({
  selector: 'app-fetch-bdn-file',
  templateUrl: './fetch-bdn-file.component.html',
  styleUrls: ['./fetch-bdn-file.component.scss']
})
export class FetchBdnFileComponent implements OnInit {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  activeMode: 'BDN' | 'LOP' | null = null;
  isUploading = false;
  selectedFile: File | null = null;

  // Forms
  bdnForm!: FormGroup;
  lopForm!: FormGroup;

  // Data
  products: any[] = [];
  discrepancies: any[] = [];

  // LOP specific
  bdnList: any[] = [];
  selectedBdnId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private emissionService: EmissionService,
    public dialogRef: MatDialogRef<FetchBdnFileComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  ngOnInit() {
    this.initForms();
    // Pre-fill IMO/Vessel if available
    const imo = this.dialogData?.imo_number || this.dialogData?.imo || localStorage.getItem('lop_imo') || '';
    const vesselName = this.dialogData?.vessel_name || '';

    this.bdnForm.patchValue({ imo, vessel_name: vesselName });
  }

  private initForms() {
    this.bdnForm = this.fb.group({
      vessel_name: ['', Validators.required],
      imo: ['', [Validators.required]],
      port: ['', Validators.required],
      bunkerDate: [null, Validators.required],
      bdn_number: ['', Validators.required]
    });

    this.lopForm = this.fb.group({
      lopDate: [null, Validators.required]
    });
  }

  // mode selection
  setMode(mode: 'BDN' | 'LOP') {
    this.activeMode = mode;
    if (mode === 'LOP') {
      this.fetchBdnList();
    } else {
      if (this.products.length === 0) {
        this.addProduct();
      }
    }
  }

  fetchBdnList() {
    const imo = this.bdnForm.value.imo || this.dialogData?.imo_number || this.dialogData?.imo || localStorage.getItem('lop_imo');
    if (!imo) {
      this.toastr.warning('Please enter IMO first to fetch BDNs');
      return;
    }
    this.emissionService.getBdnList(Number(imo)).subscribe({
      next: (res: any) => {
        this.bdnList = res?.documents || [];
      },
      error: () => this.toastr.error('Failed to fetch BDN list')
    });
  }

  onBdnSelect(event: any) {
    this.selectedBdnId = Number(event.target.value);
    if (this.discrepancies.length === 0) {
      this.addDiscrepancy();
    }
  }

  // File handling
  onFileSelect(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) {
      this.toastr.error('Only PDF and image files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.toastr.error('File size must be less than 5MB');
      return;
    }
    this.selectedFile = file;
  }

  removeFile() {
    this.selectedFile = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  // Product management
  addProduct() {
    this.products.push({
      product: 'VLSFO',
      qty_mt: null,
      qty_m3: null,
      density_15c: null,
      viscosity_50c_cst: null,
      sulphur_percent: null,
      flash_point_c: null
    });
  }

  removeProduct(index: number) {
    this.products.splice(index, 1);
  }

  // Discrepancy management
  addDiscrepancy() {
    this.discrepancies.push({
      fuel_type: 'VLSFO',
      bunker_qty_receipt_mt: null,
      received_qty_ship_mt: null,
      difference_mt: null
    });
  }

  removeDiscrepancy(index: number) {
    this.discrepancies.splice(index, 1);
  }

  private validatePositive(val: any): boolean {
    if (val === null || val === undefined || val === '') return true;
    return Number(val) >= 0;
  }

  // Submit
  submitBDN() {
    if (this.bdnForm.invalid) {
      this.toastr.error('Please fill all required BDN fields');
      return;
    }
    if (!this.selectedFile) {
      this.toastr.error('Please attach a file');
      return;
    }
    if (this.products.length === 0) {
      this.toastr.error('Please add at least one product');
      return;
    }

    // Validate positive numbers
    for (const p of this.products) {
      if (!this.validatePositive(p.qty_mt) || !this.validatePositive(p.qty_m3) || 
          !this.validatePositive(p.density_15c) || !this.validatePositive(p.viscosity_50c_cst) || 
          !this.validatePositive(p.sulphur_percent) || !this.validatePositive(p.flash_point_c)) {
        this.toastr.error('Product quantities and properties must be positive numbers');
        return;
      }
    }

    const payload = {
      ...this.bdnForm.value,
      delivery_date: this.bdnForm.value.bunkerDate,
      products: this.products
    };

    this.isUploading = true;
    this.emissionService.manualUploadBDN(this.selectedFile, payload).subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.toastr.success('BDN uploaded successfully');
        this.dialogRef.close({ verified: 'BDN', docId: res.doc_id });
      },
      error: (err: any) => {
        this.isUploading = false;
        this.toastr.error(err?.error?.error || 'BDN upload failed');
      }
    });
  }

  submitLOP() {
    if (this.lopForm.invalid) {
      this.toastr.error('Please fill all required LOP fields');
      return;
    }
    if (!this.selectedBdnId) {
      this.toastr.error('Please select a BDN');
      return;
    }
    if (!this.selectedFile) {
      this.toastr.error('Please attach a file');
      return;
    }
    if (this.discrepancies.length === 0) {
      this.toastr.error('Please add at least one discrepancy');
      return;
    }

    // Validate positive numbers
    for (const d of this.discrepancies) {
      if (!this.validatePositive(d.bunker_qty_receipt_mt) || !this.validatePositive(d.received_qty_ship_mt)) {
        this.toastr.error('Quantities must be positive numbers');
        return;
      }
    }

    const payload = {
      imo: this.bdnForm.value.imo || this.dialogData?.imo_number || this.dialogData?.imo || localStorage.getItem('lop_imo'),
      lop_date: this.lopForm.value.lopDate,
      discrepancies: this.discrepancies
    };

    this.isUploading = true;
    this.emissionService.manualUploadLOP(this.selectedBdnId, this.selectedFile, payload).subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.toastr.success('LOP uploaded successfully');
        this.dialogRef.close({ verified: 'LOP', docId: res.doc_id });
      },
      error: (err: any) => {
        this.isUploading = false;
        this.toastr.error(err?.error?.error || 'LOP upload failed');
      }
    });
  }

  goBack() {
    this.activeMode = null;
    this.selectedFile = null;
    this.products = [];
    this.discrepancies = [];
    this.selectedBdnId = null;
    this.bdnForm.reset();
    this.lopForm.reset();
    this.ngOnInit();
  }
}
